import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usStates, loanBenefits, loanFaqs } from '../mock';
import { publicApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Zap, Lock, Sliders, ShieldCheck, Check, ArrowRight, DollarSign, GitCompare, BadgeCheck, BookOpen, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import FaqAccordion from '../components/shared/FaqAccordion';
import CtaBlock from '../components/shared/CtaBlock';
import { buildQueryString, saveUserParams, getSessionId } from '../lib/userParams';

const iconMap = { Zap, Lock, Sliders, GitCompare, BadgeCheck, BookOpen };

const REDIRECT_SECONDS = 60;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Loan() {
  const [amount, setAmount] = useState([10000]);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', state: '', zip: '' });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Countdown effect once success dialog opens
  useEffect(() => {
    if (!successOpen) return;
    if (countdown <= 0) {
      setSuccessOpen(false);
      navigate(`/protect-credit?${buildRedirectQuery()}`);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successOpen, countdown, navigate]);

  const buildRedirectQuery = () => buildQueryString({
    firstName: form.firstName,
    lastName: form.lastName,
    name: `${form.firstName} ${form.lastName}`.trim(),
    email: form.email,
    phone: form.phone,
    zip: form.zip,
    state: form.state,
    loanAmount: amount[0],
  });

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = 'Please enter a valid email address';
    if (!form.phone.trim()) errs.phone = 'Phone is required';
    else if (form.phone.replace(/\D/g, '').length !== 10) errs.phone = 'Phone must be exactly 10 digits';
    if (!form.state) errs.state = 'Please select your state';
    if (!form.zip.trim()) errs.zip = 'ZIP code is required';
    else if (!/^\d{5}$/.test(form.zip.trim())) errs.zip = 'ZIP must be exactly 5 digits';
    if (!agree) errs.agree = 'You must accept the terms to continue';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast({ title: 'Please fix the errors below' });
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitForm('loan', { ...form, loanAmount: amount[0], sessionId: getSessionId() });
      // Fire Meta Pixel Lead conversion event for ad optimization
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Personal Loan Application',
          content_category: 'personal_loan',
          value: amount[0],
          currency: 'USD',
        });
      }
      // Persist user params for later pages / offers.
      // loanAmount is intentionally NOT propagated — it stays with the submission.
      saveUserParams({
        firstName: form.firstName,
        lastName: form.lastName,
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        zip: form.zip,
        state: form.state,
      });
      setCountdown(REDIRECT_SECONDS);
      setSuccessOpen(true);
    } catch (err) {
      toast({ title: 'Submission failed', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setField('phone', raw);
  };

  const handleZipChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 5);
    setField('zip', raw);
  };

  const goProtectNow = () => {
    setSuccessOpen(false);
    navigate(`/protect-credit?${buildRedirectQuery()}`);
  };

  return (
    <>
      {/* Hero + Form */}
      <section className="relative overflow-hidden bg-hero-glow">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 shadow-sm text-xs font-semibold text-emerald-700 uppercase tracking-wider">Compare • Choose • Save</div>
              <h1 className="font-serif-display mt-5 sm:mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-900 font-bold leading-[1.1]">Compare Personal Loans Up to <span className="text-emerald-600">$50,000</span></h1>
              <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-xl">See real rates from vetted lenders side by side. Enter your details once — get personalized offers in minutes without impacting your credit score.</p>
              <ul className="mt-6 space-y-2.5">
                {['Real APRs from vetted lenders, side by side','Soft credit pull — won\'t affect your score','Funds as fast as the next business day','No fees to compare. Ever.'].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-slate-700"><Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" /> {b}</li>
                ))}
              </ul>
              <div className="mt-8 flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Checking your rate won't affect your credit score</div>
            </div>

            {/* Application card */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-white shadow-2xl shadow-emerald-900/10 border border-slate-100 p-6 lg:p-7 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-emerald-100 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-serif-display text-xl font-bold text-slate-900">Get Personalized Loan Offers</h3>
                      <p className="text-xs text-slate-500">Compare rates from multiple lenders in minutes</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div>
                      <div className="flex items-baseline justify-between">
                        <Label className="text-sm font-semibold text-slate-800">Loan Amount</Label>
                        <span className="font-serif-display text-2xl font-bold text-emerald-700">${amount[0].toLocaleString()}</span>
                      </div>
                      <div className="mt-3">
                        <Slider value={amount} min={1000} max={50000} step={500} onValueChange={setAmount} />
                        <div className="flex justify-between text-xs text-slate-500 mt-1.5">
                          <span>$1,000</span><span>$50,000</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">First Name <span className="text-red-500">*</span></Label>
                        <Input value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} className={`mt-1 h-10 ${errors.firstName ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
                        {errors.firstName && <p className="text-[11px] text-red-500 mt-1">{errors.firstName}</p>}
                      </div>
                      <div>
                        <Label className="text-xs">Last Name <span className="text-red-500">*</span></Label>
                        <Input value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} className={`mt-1 h-10 ${errors.lastName ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
                        {errors.lastName && <p className="text-[11px] text-red-500 mt-1">{errors.lastName}</p>}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Email Address <span className="text-red-500">*</span></Label>
                      <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className={`mt-1 h-10 ${errors.email ? 'border-red-400 focus-visible:ring-red-400' : ''}`} placeholder="you@example.com" />
                      {errors.email && <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label className="text-xs">Phone Number <span className="text-red-500">*</span> <span className="text-slate-400">(10 digits)</span></Label>
                      <Input type="tel" inputMode="numeric" value={form.phone} onChange={handlePhoneChange} maxLength={10} className={`mt-1 h-10 ${errors.phone ? 'border-red-400 focus-visible:ring-red-400' : ''}`} placeholder="5551234567" />
                      {errors.phone && <p className="text-[11px] text-red-500 mt-1">{errors.phone}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">State <span className="text-red-500">*</span></Label>
                        <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                          <SelectTrigger className={`mt-1 h-10 ${errors.state ? 'border-red-400' : ''}`}><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent className="max-h-64">
                            {usStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-[11px] text-red-500 mt-1">{errors.state}</p>}
                      </div>
                      <div>
                        <Label className="text-xs">ZIP Code <span className="text-red-500">*</span> <span className="text-slate-400">(5 digits)</span></Label>
                        <Input inputMode="numeric" value={form.zip} onChange={handleZipChange} maxLength={5} className={`mt-1 h-10 ${errors.zip ? 'border-red-400 focus-visible:ring-red-400' : ''}`} placeholder="10001" />
                        {errors.zip && <p className="text-[11px] text-red-500 mt-1">{errors.zip}</p>}
                      </div>
                    </div>

                    <div className="pt-1">
                      <label className={`flex items-start gap-2 text-xs cursor-pointer ${errors.agree ? 'text-red-500' : 'text-slate-600'}`}>
                        <Checkbox checked={agree} onCheckedChange={(v) => { setAgree(!!v); if (errors.agree) setErrors((e) => ({ ...e, agree: undefined })); }} className={`mt-0.5 ${errors.agree ? 'border-red-400' : ''}`} />
                        <span>By submitting this form, I agree to the Terms of Service, Privacy Policy, and consent to receive communications. <span className="text-red-500">*</span></span>
                      </label>
                      {errors.agree && <p className="text-[11px] text-red-500 mt-1 ml-6">{errors.agree}</p>}
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white group">
                      {submitting ? 'Submitting…' : 'Compare My Offers'}
                      {!submitting && <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
                    </Button>
                    <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500"><Lock className="w-3.5 h-3.5" /> Your information is secure and encrypted</div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Why Compare on The Loan Alliance</h2>
            <p className="mt-4 text-slate-600">Real numbers, editor-vetted lenders, and zero pressure — the tools you need to pick the right loan.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {loanBenefits.map((b) => {
              const Icon = iconMap[b.icon];
              return (
                <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-emerald-700" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 font-bold">Frequently Asked Questions</h2>
            <p className="mt-3 text-slate-600">Find answers to common questions about our personal loan services.</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 px-6 lg:px-8">
            <FaqAccordion items={loanFaqs} />
          </div>
        </div>
      </section>

      <CtaBlock title="Secure Your Financial Future Today" subtitle="Our partners offer industry-leading rates and exclusive benefits" />

      {/* Success dialog with 60s auto-redirect to /protect-credit */}
      <Dialog open={successOpen} onOpenChange={(o) => { if (!o) setSuccessOpen(false); }}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-emerald-200">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-8 text-center text-white">
            <div className="w-14 h-14 rounded-full bg-white/20 border border-white/30 flex items-center justify-center mx-auto backdrop-blur">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="font-serif-display mt-4 text-2xl font-bold">We've received your details!</h3>
            <p className="mt-2 text-sm text-emerald-50 leading-relaxed">
              Thank you for choosing The Loan Alliance. Our team will match you with the best loan offers shortly.
            </p>
          </div>
          <div className="p-6">
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold text-emerald-800">Next step:</span> While you wait, we recommend you check your <span className="font-semibold">Credit Health</span> to stay up-to-date with your credit history and protect it against fraud.
              </p>
            </div>
            <div className="mt-5 relative h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-1000 ease-linear"
                style={{ width: `${((REDIRECT_SECONDS - countdown) / REDIRECT_SECONDS) * 100}%` }}
              />
            </div>
            <p className="mt-2 text-center text-xs text-slate-500">
              Redirecting to <span className="font-semibold text-slate-700">Protect Credit</span> in{' '}
              <span className="font-semibold text-emerald-700 tabular-nums">{countdown}s</span>
            </p>
            <Button onClick={goProtectNow} className="w-full mt-4 h-11 bg-emerald-600 hover:bg-emerald-700 text-white group">
              Check My Credit Health Now
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
